// View screen — read-only detail of one visit. RON-5466 Phase 3. Prototype: `ViewScreen`.
//
// Reachable for EVERY status, including cancelled: a closed visit stays readable rather than
// becoming inert (confirmed with the team). What changes by status is the actions, not the detail —
// Edit and Cancel are offered only when `canEditVisit` / `canCancelVisit` allow them, so this
// screen never presents a button that would fail.
//
// LAYOUT follows the prototype: a header row carrying the guest's identity on the left and the
// actions on the right, then two detail cards SIDE BY SIDE — who is visiting, and the visit itself.
// The guest's name is the page heading, so `VisitorUI` adds no `module-title` above this screen.

import { useTranslation } from "react-i18next";

import { formatDateToEEEddMMM } from "../../../utils/CommonUtils.js";
import {
  VISIT_KIND,
  VISIT_TYPE,
  activityLabelKey,
  canCancelVisit,
  canEditVisit,
} from "../visitorConstants.js";
import {
  VisitorAvatar,
  VisitorKindBadge,
  VisitorProvisionalBadge,
  VisitorStatusBadge,
  VisitorVipBadge,
} from "./VisitorBadges.jsx";
import VisitorDetailRow from "./VisitorDetailRow.jsx";
import { formatISOToLong } from "../visitorDateUtils.js";
import clockIcon from "../../../assets/images/icons/booking-clock.svg";

const VisitorDetail = ({
  visit,
  groupMembers = [],
  onEdit,
  onCancel,
  onBack,
}) => {
  const { t } = useTranslation("common");

  // Guard rather than crash: a direct navigation with no selection should return to the list, not
  // blow up the route.
  if (!visit) {
    return (
      <div className="visitor-detail">
        <p className="visitor__placeholder">{t("react_visitors_not_found")}</p>
        <button
          type="button"
          className="visitor-btn visitor-btn--secondary"
          onClick={onBack}
        >
          {t("react_visitors_back_to_list")}
        </button>
      </div>
    );
  }

  const isContractor = visit.kind === VISIT_KIND.CONTRACTOR;
  const editable = canEditVisit(visit);
  const cancellable = canCancelVisit(visit);

  const activity = Array.isArray(visit.activity) ? visit.activity : [];

  const typeLabel =
    visit.type === VISIT_TYPE.VIP
      ? t("react_visitors_vip")
      : t("react_visitors_type_standard");

  // Who is visiting. The name repeats the header on purpose — the prototype does this so the card
  // reads as a complete record of the guest rather than a continuation of the heading.
  // Short generic labels ("Name", "Email", "Company") rather than the wizard's fuller ones
  // ("Visitor name", "Email address"): inside a card already titled VISITOR DETAILS the longer
  // form repeats itself. Matches the prototype, and reuses keys the list header already uses.
  const guestRows = [
    [t("name"), visit.name],
    [t("email"), visit.email],
    [t("company"), visit.company],
    // Trade is a contractor-only column on the visit (db-schema §5).
    isContractor && visit.trade ? [t("react_visitors_field_trade"), visit.trade] : null,
    // REMOVED 07-October-2026. The "IT equipment" row was here, reading `itProvided`.
    //
    // It had no backing column anywhere and the real API never sent it, so once the module went
    // live the row read "No" for every visitor — a confident answer to a question nobody had
    // recorded. Removed by product decision rather than left showing invented data.
    // Grouped with the guest, not the visit: VIP is a property of the person being hosted.
    [t("react_visitors_field_visitor_type"), typeLabel],
  ].filter(Boolean);

  // Start and end are BOTH always shown, even for a same-day visit, so the card keeps a stable
  // shape and "one day" is stated rather than inferred from a missing row.
  const visitRows = [
    [t("react_visitors_field_start_date"), formatDateToEEEddMMM(visit.startDate)],
    [t("react_visitors_field_end_date"), formatDateToEEEddMMM(visit.endDate)],
    [t("react_visitors_field_arrival"), visit.arrivalTime],
    [t("react_visitors_field_site"), visit.site || "—"],
    visit.building ? [t("react_visitors_field_building"), visit.building] : null,
    [t("react_visitors_field_host"), visit.host],
    visit.bookingCode
      ? [t("react_visitors_field_booking_code"), visit.bookingCode]
      : null,
  ].filter(Boolean);

  // Vetting only means something for a contractor.
  const vettingRows = isContractor
    ? [
        [
          t("react_visitors_field_vetting"),
          visit.isProvisional
            ? t("react_visitors_vetting_provisional")
            : t("react_visitors_vetting_cleared"),
        ],
        visit.vettedUntil
          ? [
              t("react_visitors_field_vetted_until"),
              formatISOToLong(visit.vettedUntil, t),
            ]
          : null,
      ].filter(Boolean)
    : [];

  return (
    <div className="visitor-detail">
      {/* Back is a quiet link, not a pill: the prototype's header has no way out, but this screen is
          a step in a state machine rather than a route, so the browser's own back button does not
          return to the list. */}
      <button type="button" className="visitor-detail__back" onClick={onBack}>
        {t("react_visitors_back_to_list")}
      </button>

      <div className="visitor-detail__header">
        <div className="visitor-detail__identity">
          <VisitorAvatar name={visit.name} type={visit.type} />
          <div className="visitor-detail__identity-text">
            <h2 className="visitor-detail__name">{visit.name}</h2>
            {/* Status first, matching the prototype: what state the visit is in matters more at a
                glance than whether they are a visitor or a contractor. */}
            <span className="visitor-detail__badges">
              <VisitorStatusBadge status={visit.status} />
              <VisitorKindBadge kind={visit.kind} />
              {visit.type === VISIT_TYPE.VIP && <VisitorVipBadge />}
              {visit.isProvisional && <VisitorProvisionalBadge />}
            </span>
          </div>
        </div>

        <div className="visitor-detail__actions">
          {editable && (
            <button
              type="button"
              className="visitor-btn visitor-btn--secondary"
              onClick={() => onEdit?.(visit)}
            >
              {/* "Edit", matching the prototype and this module's own list row action. */}
              {t("edit")}
            </button>
          )}
          {cancellable && (
            <button
              type="button"
              className="visitor-btn visitor-btn--secondary visitor-btn--danger"
              onClick={() => onCancel?.(visit)}
            >
              {t("react_visitors_cancel_booking")}
            </button>
          )}
        </div>
      </div>

      <div className="visitor-detail__columns">
        <div className="visitor-card">
          <p className="visitor-card__title">{t("react_visitors_details_title")}</p>
          {guestRows.map(([label, value]) => (
            <VisitorDetailRow key={label} label={label} value={value} />
          ))}
        </div>

        <div className="visitor-card">
          <p className="visitor-card__title">{t("react_visitors_visit_title")}</p>
          {visitRows.map(([label, value]) => (
            <VisitorDetailRow key={label} label={label} value={value} />
          ))}
        </div>
      </div>

      {vettingRows.length > 0 && (
        <div className="visitor-card">
          <p className="visitor-card__title">{t("react_visitors_vetting_title")}</p>
          {vettingRows.map(([label, value]) => (
            <VisitorDetailRow key={label} label={label} value={value} />
          ))}
        </div>
      )}

      {/* DUMMY DATA. The panel is real; `visit.activity` is fixture-only because
          `tblvisitor_visit` has no audit columns. Raised for the API work — an entry needs an actor
          (reception checks in, the host cancels) and, for edits, a field-level diff. See
          docs/VisitorDetail.md. */}
      {activity.length > 0 && (
        <div className="visitor-card">
          <p className="visitor-card__title">{t("react_visitors_activity_title")}</p>
          <ul className="visitor-activity">
            {activity.map((entry, index) => (
              <li className="visitor-activity__item" key={index}>
                <img
                  src={clockIcon}
                  alt=""
                  className="visitor-activity__icon"
                  aria-hidden="true"
                />
                <span className="visitor-activity__event">
                  {t(activityLabelKey(entry.event))}
                </span>
                <span className="visitor-activity__meta">
                  {formatDateToEEEddMMM(entry.at)}
                  {entry.by ? ` · ${entry.by}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Everyone else on the same booking. Shown as a panel rather than a one-line count so the
          host can see WHO is affected before editing or cancelling this visit. */}
      {groupMembers.length > 0 && (
        <div className="visitor-card">
          <p className="visitor-card__title">
            {t("react_visitors_invited_together").replace(
              "%s",
              String(groupMembers.length)
            )}
          </p>
          <ul className="visitor-activity">
            {groupMembers.map((member) => (
              <li className="visitor-group-member" key={member.id}>
                <VisitorAvatar name={member.name} type={member.type} />
                <span className="visitor-group-member__name">{member.name}</span>
                <span className="visitor-group-member__date">
                  {formatDateToEEEddMMM(member.startDate)}
                </span>
                <VisitorStatusBadge status={member.status} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default VisitorDetail;
